import React from 'react';
import {
  Info, Clock, Flame, Activity, Plane, CheckCircle2, RefreshCw,
} from 'lucide-react';
import { Robot } from '@phosphor-icons/react';

import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

import MetricCard from '../../components/projects/MetricCard';
import AodbBenchmarkSection from '../../components/benchmarking/AodbBenchmarkSection';
import { ErrorBoundary } from '../../components/common/ErrorBoundary';

import { useProject } from './ProjectContext';
import { useProjectAnalysis } from './useProjectAnalysis';

const KpisPage: React.FC = () => {
  const { project, reload } = useProject();
  const { apcas } = useProjectAnalysis();
  const { theme } = useTheme();
  // Still needed: MetricCard and AodbBenchmarkSection require isDark as a prop.
  const isDark = theme === 'dark';
  const { language } = useLanguage();

  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';

  return (
    <ErrorBoundary showDetails={true}>
      <div className="space-y-6 animate-fade-in">

        {/* SAFEGUARD: Check for Advanced Analysis Data */}
        {!project?.analysis_results?.advanced_analysis && (
          <div
            className="p-4 rounded-lg border flex items-center gap-3"
            style={{
              background: 'color-mix(in srgb, var(--aipron-warning) 10%, transparent)',
              borderColor: 'color-mix(in srgb, var(--aipron-warning) 35%, transparent)',
            }}
          >
            <Info style={{ color: 'var(--aipron-warning)' }} />
            <div>
              <p className={`font-bold ${textMain}`}>
                {language === 'es' ? 'Datos avanzados no disponibles' : 'Advanced data not available'}
              </p>
              <p className={`text-sm ${textSub}`}>
                {language === 'es'
                  ? 'Por favor, haz clic en el botón "Re-ejecutar Análisis" en el encabezado para generar los nuevos mapas y KPIs.'
                  : 'Please click the "Re-run Analysis" button in the header to generate new maps and KPIs.'}
              </p>
            </div>
          </div>
        )}

        {/* The blue-time / congestion / complexity-index tiles moved to the top of
            the Mapas module (they read the same spatial taxi behaviour the map
            shows). */}

        {/* Operational Efficiency KPIs - 2 ROWS LAYOUT */}
        <div className="pro-card p-6">
          <h3 className={`font-bold text-lg mb-4 ${textMain}`}>Operational Efficiency KPIs</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Avg Apron Taxi Duration */}
            <MetricCard title="Apron Taxi-In"
              value={`${(project?.analysis_results?.advanced_analysis?.operational_kpis?.median_axit || 0).toFixed(1)} min`}
              sub="AIBT - ATC/Apron Handover" icon={Plane} isDark={isDark} color="blue"
              description="Tiempo mediano de rodaje de llegada desde el punto de handover ATC hasta el stand (In-Block)." />

            <MetricCard title="Apron Taxi-Out"
              value={`${(project?.analysis_results?.advanced_analysis?.operational_kpis?.median_axot || 0).toFixed(1)} min`}
              sub="Apron↔ATC Handover - AOBT" icon={Plane} isDark={isDark} color="blue"
              description="Tiempo mediano de rodaje de salida desde el stand (Off-Block) hasta el punto de handover ATC." />

            <MetricCard title="Avg Taxi Speed"
              value={`${(project?.analysis_results?.advanced_analysis?.operational_kpis?.avg_taxi_speed || 0).toFixed(1)} kts`}
              sub="Median Groundspeed" icon={Flame} isDark={isDark} color="purple"
              description="Velocidad mediana de rodaje en la plataforma. Velocidades bajas indican congestión." />

            <MetricCard title={language === 'es' ? 'Retraso Off-Block' : 'Off-Block Delay'}
              value={`${(project?.analysis_results?.advanced_analysis?.operational_kpis?.tobt_adherence || 0).toFixed(1)} min`}
              sub="Avg (AOBT − EOBT)" icon={Clock} isDark={isDark} color="orange"
              description={language === 'es'
                ? 'Retraso de pushback respecto a la hora programada (AOBT − EOBT). En aeropuertos sin A-CDM, EOBT = SOBT = STD. Positivo = retrasado, negativo = adelantado. TOBT Adherence (A-CDM real) requiere TOBT ≠ STD asignado por ATC.'
                : 'Off-block delay vs scheduled time (AOBT − EOBT). In non-A-CDM airports EOBT = SOBT = STD. Positive = late, negative = early. True TOBT Adherence requires ATC-assigned TOBT ≠ STD.'} />

            <MetricCard title="Pushback Delay"
              value={`${(project?.analysis_results?.advanced_analysis?.operational_kpis?.pushback_delay || 0).toFixed(1)} min`}
              sub="Avg (AOBT - ASAT)" icon={Clock} isDark={isDark} color="orange"
              description="Tiempo de reacción del equipo de tierra/tractor desde ASAT (Ready) hasta el inicio real del pushback." />

            <MetricCard title="Flow Adherence"
              value={`${(project?.analysis_results?.advanced_analysis?.operational_kpis?.flow_adherence || 0).toFixed(1)} min`}
              sub="Avg (AOBT - TSAT)" icon={Activity} isDark={isDark} color="red"
              description="Cálculo fundamental A-CDM. TSAT = ventana asignada por secuencia de pista. Fuera de ±5min = pérdida de slot. Crítico para EUROCONTROL." />

            <MetricCard title="Stand Occupancy %"
              value={`${(project?.analysis_results?.advanced_analysis?.operational_kpis?.occupancy_compliance || 0).toFixed(1)}%`}
              sub="Target: ≥ 90%" icon={CheckCircle2} isDark={isDark} color="green"
              description="Porcentaje de aeronaves que desocupan el stand dentro de la ventana SIBT-SOBT asignada." />

            <MetricCard title="Stand Reassignment"
              value={`${(project?.analysis_results?.advanced_analysis?.operational_kpis?.stand_changes || 0).toFixed(1)}%`}
              sub="Tactical vs Planned" icon={RefreshCw} isDark={isDark} color="yellow"
              description="Ratio de cambios de stand no planificados vs Stand Allocation Plan original." />

            <MetricCard title="Taxi Route Adherence"
              value={`${project?.analysis_results?.advanced_analysis?.operational_kpis?.route_deviations || '0'}`}
              sub="Target: 0 incidents" icon={Info} isDark={isDark} color="green"
              description="Desviaciones de rutas de rodaje o procedimientos de pushback asignados por AMS." />
          </div>
        </div>

        {/* AODB Operational KPIs + GOscore Benchmarking */}
        <AodbBenchmarkSection
          project={project}
          isDark={isDark}
          onProjectUpdate={reload}
        />

        {/* The interactive heatmap map and the TCF / complexity cards moved to the
            "Mapas" module (they are spatial views of the airport); this module
            keeps the operational KPI data. */}

        {/* ── Layer 2: Impact Correlation Panel ── */}
        {apcas.block2?.layer2?.available && (
          <div className="pro-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-cyan-50 dark:bg-cyan-900/30">
                <Activity className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <h3 className={`font-bold text-lg ${textMain}`}>
                  {language === 'es' ? 'Correlación de Impacto Operativo (Layer 2)' : 'Operational Impact Correlation (Layer 2)'}
                </h3>
                <p className={`text-xs ${textSub}`}>
                  {language === 'es'
                    ? 'Cruza condiciones meteorológicas/NOTAMs con desviaciones operativas reales (AXIT, AXOT, ACT_TURN, DEP_DELAY)'
                    : 'Cross-references weather/NOTAM conditions with actual operational deviations (AXIT, AXOT, ACT_TURN, DEP_DELAY)'}
                </p>
              </div>
              <div className="ml-auto px-3 py-1 rounded-full text-xs font-bold bg-cyan-50 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-500/30">
                {(apcas.block2.layer2.layers_used || []).map((l: number) => `L${l}`).join(' + ')}
              </div>
            </div>

            {/* Side-by-side WCF/WIF comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* WCF Comparison */}
              <div
                className="p-4 rounded-xl border"
                style={{ background: 'color-mix(in srgb, var(--aipron-info) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-info) 25%, transparent)' }}
              >
                <h4 className="text-xs font-bold uppercase mb-3" style={{ color: 'var(--aipron-info)' }}>
                  WCF — Weather Conditions Factor
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[var(--surface-raised)]">
                    <p className={`text-[10px] uppercase font-bold ${textSub}`}>Layer 1 (METAR)</p>
                    <p className="text-xl font-black font-mono tabular-nums" style={{ color: 'var(--aipron-info)' }}>
                      {((apcas.block2.layer2.wcf_meteo || 0) * 100).toFixed(1)}%
                    </p>
                    <p className={`text-[10px] ${textSub}`}>
                      {apcas.block2.layer2.weather_adverse_days || 0} {language === 'es' ? 'días adversos' : 'adverse days'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-[var(--surface-raised)]">
                    <p className={`text-[10px] uppercase font-bold ${textSub}`}>Layer 2 (Ops)</p>
                    <p className="text-xl font-black font-mono tabular-nums text-cyan-600 dark:text-cyan-300">
                      {apcas.block2.layer2.wcf_ops != null ? ((apcas.block2.layer2.wcf_ops) * 100).toFixed(1) + '%' : '—'}
                    </p>
                    <p className={`text-[10px] ${textSub}`}>
                      {apcas.block2.layer2.weather_and_degraded_days || 0} {language === 'es' ? 'días confirmados' : 'confirmed days'}
                    </p>
                  </div>
                </div>
                {/* Arrow showing reduction */}
                {apcas.block2.layer2.wcf_ops != null && apcas.block2.layer2.wcf_meteo > 0 && (
                  <div className="mt-2 flex items-center justify-center gap-2 text-xs font-bold" style={{ color: 'var(--aipron-success)' }}>
                    <span>↓ {((1 - apcas.block2.layer2.wcf_ops / apcas.block2.layer2.wcf_meteo) * 100).toFixed(0)}%</span>
                    <span className={`font-normal ${textSub}`}>
                      {language === 'es' ? 'días sin degradación real filtrados' : 'days without real degradation filtered'}
                    </span>
                  </div>
                )}
              </div>

              {/* WIF Comparison */}
              <div
                className="p-4 rounded-xl border"
                style={{ background: 'color-mix(in srgb, var(--aipron-danger) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-danger) 25%, transparent)' }}
              >
                <h4 className="text-xs font-bold uppercase mb-3" style={{ color: 'var(--aipron-danger)' }}>
                  WIF — Works Impact Factor
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[var(--surface-raised)]">
                    <p className={`text-[10px] uppercase font-bold ${textSub}`}>Layer 1 (NOTAM)</p>
                    <p className="text-xl font-black font-mono tabular-nums" style={{ color: 'var(--aipron-danger)' }}>
                      {((apcas.block2.layer2.wif_notam || 0) * 100).toFixed(1)}%
                    </p>
                    <p className={`text-[10px] ${textSub}`}>
                      {apcas.block2.layer2.notam_active_days || 0} {language === 'es' ? 'días con NOTAM' : 'NOTAM-active days'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-[var(--surface-raised)]">
                    <p className={`text-[10px] uppercase font-bold ${textSub}`}>Layer 2 (Ops)</p>
                    <p className="text-xl font-black font-mono tabular-nums" style={{ color: 'var(--aipron-accent)' }}>
                      {apcas.block2.layer2.wif_ops != null ? ((apcas.block2.layer2.wif_ops) * 100).toFixed(1) + '%' : '—'}
                    </p>
                    <p className={`text-[10px] ${textSub}`}>
                      {apcas.block2.layer2.notam_and_degraded_days || 0} {language === 'es' ? 'días confirmados' : 'confirmed days'}
                    </p>
                  </div>
                </div>
                {apcas.block2.layer2.wif_ops != null && apcas.block2.layer2.wif_notam > 0 && (
                  <div className="mt-2 flex items-center justify-center gap-2 text-xs font-bold" style={{ color: 'var(--aipron-success)' }}>
                    <span>↓ {((1 - apcas.block2.layer2.wif_ops / apcas.block2.layer2.wif_notam) * 100).toFixed(0)}%</span>
                    <span className={`font-normal ${textSub}`}>
                      {language === 'es' ? 'días sin degradación real filtrados' : 'days without real degradation filtered'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Resilience Indices + Day Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg text-center bg-[var(--surface-raised)]">
                <p className={`text-[10px] uppercase font-bold ${textSub}`}>
                  {language === 'es' ? 'Resiliencia Meteo' : 'Weather Resilience'}
                </p>
                <p
                  className="text-2xl font-black font-mono tabular-nums"
                  style={{ color: (apcas.block2.layer2.resilience_weather || 0) < 0.5 ? 'var(--aipron-success)' :
                    (apcas.block2.layer2.resilience_weather || 0) < 0.8 ? 'var(--aipron-warning)' : 'var(--aipron-danger)' }}
                >
                  {apcas.block2.layer2.resilience_weather != null
                    ? `${(apcas.block2.layer2.resilience_weather * 100).toFixed(0)}%`
                    : '—'}
                </p>
                <p className={`text-[10px] ${textSub}`}>
                  {language === 'es' ? '% días meteo que afectaron' : '% weather days that impacted'}
                </p>
              </div>
              <div className="p-3 rounded-lg text-center bg-[var(--surface-raised)]">
                <p className={`text-[10px] uppercase font-bold ${textSub}`}>
                  {language === 'es' ? 'Resiliencia Obras' : 'Works Resilience'}
                </p>
                <p
                  className="text-2xl font-black font-mono tabular-nums"
                  style={{ color: (apcas.block2.layer2.resilience_works || 0) < 0.5 ? 'var(--aipron-success)' :
                    (apcas.block2.layer2.resilience_works || 0) < 0.8 ? 'var(--aipron-warning)' : 'var(--aipron-danger)' }}
                >
                  {apcas.block2.layer2.resilience_works != null
                    ? `${(apcas.block2.layer2.resilience_works * 100).toFixed(0)}%`
                    : '—'}
                </p>
                <p className={`text-[10px] ${textSub}`}>
                  {language === 'es' ? '% días NOTAM que afectaron' : '% NOTAM days that impacted'}
                </p>
              </div>
              <div className="p-3 rounded-lg text-center bg-[var(--surface-raised)]">
                <p className={`text-[10px] uppercase font-bold ${textSub}`}>
                  {language === 'es' ? 'Total Días' : 'Total Days'}
                </p>
                <p className={`text-2xl font-black font-mono tabular-nums ${textMain}`}>
                  {apcas.block2.layer2.total_days || 0}
                </p>
                <p className={`text-[10px] ${textSub}`}>
                  {language === 'es' ? 'en el dataset' : 'in the dataset'}
                </p>
              </div>
              <div className="p-3 rounded-lg text-center bg-[var(--surface-raised)]">
                <p className={`text-[10px] uppercase font-bold ${textSub}`}>
                  {language === 'es' ? 'Días Degradados' : 'Degraded Days'}
                </p>
                <p className="text-2xl font-black font-mono tabular-nums" style={{ color: 'var(--aipron-warning)' }}>
                  {apcas.block2.layer2.ops_degraded_days || 0}
                </p>
                <p className={`text-[10px] ${textSub}`}>
                  {language === 'es' ? '≥2 KPIs con z≥1.5σ' : '≥2 KPIs with z≥1.5σ'}
                </p>
              </div>
            </div>

            {/* ── Layer 3: AI-RCA Attribution Panel ── */}
            {apcas.block2?.layer2?.available && (() => {
              const l2 = apcas.block2.layer2;
              const attributions = l2.day_attributions || [];
              const hasL3 = attributions.length > 0;
              const avgConf = l2.attribution_confidence || 0;

              if (!hasL3) {
                // Teaser: L3 not yet run
                return (
                  <div className="mt-4 p-3 rounded-lg text-xs flex items-center gap-2 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-500/20 text-purple-700 dark:text-purple-300">
                    <Robot size={16} weight="duotone" />
                    <span className="font-bold">Layer 3 (AI-RCA):</span>
                    <span className={textSub}>
                      {language === 'es'
                        ? `Atribución causal con IA disponible para ${l2.ops_degraded_days || 0} días degradados. Se ejecuta automáticamente con API key.`
                        : `AI causal attribution available for ${l2.ops_degraded_days || 0} degraded days. Runs automatically with API key.`}
                    </span>
                  </div>
                );
              }

              // Full L3 panel
              // Aggregate cause weights
              const causeTotals: Record<string, number> = {};
              const causeColors: Record<string, string> = {
                WEATHER: 'text-[var(--aipron-info)]',
                WORKS: 'text-[var(--aipron-danger)]',
                ATFM: 'text-[var(--aipron-warning)]',
                TECHNICAL: 'text-[var(--aipron-accent)]',
                INDUSTRIAL: 'text-purple-600 dark:text-purple-400',
                OTHER: textSub,
              };
              const causeBgs: Record<string, string> = {
                WEATHER: 'bg-blue-500',
                WORKS: 'bg-red-500',
                ATFM: 'bg-yellow-500',
                TECHNICAL: 'bg-orange-500',
                INDUSTRIAL: 'bg-purple-500',
                OTHER: 'bg-gray-500',
              };

              attributions.forEach((a: any) => {
                (a.causes || []).forEach((c: any) => {
                  causeTotals[c.category] = (causeTotals[c.category] || 0) + (c.weight || 0);
                });
              });
              const n = attributions.length;
              const causeAvgs = Object.entries(causeTotals)
                .map(([cat, total]) => ({ category: cat, avg: total / n }))
                .filter(c => c.avg > 0.01)
                .sort((a, b) => b.avg - a.avg);

              // Top 5 days by confidence
              const topDays = [...attributions]
                .sort((a: any, b: any) => (b.confidence || 0) - (a.confidence || 0))
                .slice(0, 5);

              return (
                <div className="mt-4 p-5 rounded-xl border bg-purple-50/50 dark:bg-purple-900/5 border-purple-200 dark:border-purple-500/20">
                  <div className="flex items-center gap-3 mb-4">
                    <Robot size={28} weight="duotone" className="text-purple-600 dark:text-purple-400" />
                    <div>
                      <h4 className={`font-bold text-sm ${textMain}`}>
                        Layer 3 — AI Root Cause Attribution
                      </h4>
                      <p className={`text-xs ${textSub}`}>
                        {language === 'es'
                          ? `${n} días analizados con Gemini · Confianza media: ${(avgConf * 100).toFixed(0)}%`
                          : `${n} days analyzed with Gemini · Avg confidence: ${(avgConf * 100).toFixed(0)}%`}
                      </p>
                    </div>
                    <div className="ml-auto px-3 py-1 rounded-full text-xs font-bold bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-500/30">
                      L1 + L2 + L3
                    </div>
                  </div>

                  {/* Cause distribution bar */}
                  <div className="mb-4">
                    <p className={`text-[10px] uppercase font-bold mb-2 ${textSub}`}>
                      {language === 'es' ? 'Distribución de causas (promedio)' : 'Cause distribution (average)'}
                    </p>
                    <div className="flex h-5 rounded-full overflow-hidden">
                      {causeAvgs.map((c, i) => (
                        <div
                          key={c.category}
                          className={`${causeBgs[c.category] || 'bg-gray-500'} transition-all`}
                          style={{ width: `${c.avg * 100}%` }}
                          title={`${c.category}: ${(c.avg * 100).toFixed(0)}%`}
                        />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {causeAvgs.map(c => (
                        <span key={c.category} className={`text-[10px] font-bold ${causeColors[c.category] || textSub}`}>
                          ● {c.category} {(c.avg * 100).toFixed(0)}%
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Top attributed days */}
                  {topDays.length > 0 && (
                    <div>
                      <p className={`text-[10px] uppercase font-bold mb-2 ${textSub}`}>
                        {language === 'es' ? 'Días con mayor atribución' : 'Top attributed days'}
                      </p>
                      <div className="space-y-1.5">
                        {topDays.map((d: any, i: number) => (
                          <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg text-xs bg-[var(--surface-raised)]">
                            <span className={`font-mono font-bold tabular-nums w-20 ${textMain}`}>{d.day}</span>
                            <span className={`font-bold px-2 py-0.5 rounded ${causeColors[d.primary_cause] || textSub}`}>
                              {d.primary_cause}
                            </span>
                            <span className={`flex-1 truncate ${textSub}`}>
                              {d.narrative ? d.narrative.slice(0, 80) + (d.narrative.length > 80 ? '…' : '') : '—'}
                            </span>
                            <span
                              className="font-bold font-mono tabular-nums"
                              style={{ color: d.confidence >= 0.7 ? 'var(--aipron-success)' : 'var(--aipron-warning)' }}
                            >
                              {(d.confidence * 100).toFixed(0)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default KpisPage;
